import { useLayoutEffect, useRef, useState } from 'react'

import { cn } from '@/lib/utils'

/** Fixed corner radius (px) so every frame's squircle is the same shape, whatever the article size. */
export const SQUIRCLE_FRAME_RADIUS = 72

/** Half-pixel stroke inset used in squircle path geometry. */
export const SQUIRCLE_FRAME_STROKE_INSET = 0.5

/** Corner seal size — matches squircle arc extent (radius + stroke inset). */
export const SQUIRCLE_CORNER_SEAL_SIZE_PX =
  SQUIRCLE_FRAME_RADIUS + SQUIRCLE_FRAME_STROKE_INSET

/** Default stroke-draw duration for scroll-triggered squircle frames. */
export const SQUIRCLE_DRAW_DURATION_MS = 1000

/** Control-point offset of the smooth (squircle) corner, as a fraction of the radius. */
export const SQUIRCLE_FRAME_SMOOTHING = 0.18

export type SquircleFrameCorner = 'bottom-left' | 'bottom-right'

export type SquircleCornerBoxCorner =
  | 'top-right'
  | 'top-left'
  | 'bottom-left'
  | 'bottom-right'

function squircleCornerMetrics(size: number) {
  const r = Math.min(SQUIRCLE_FRAME_RADIUS, size - SQUIRCLE_FRAME_STROKE_INSET)
  const k = r * SQUIRCLE_FRAME_SMOOTHING
  const i = SQUIRCLE_FRAME_STROKE_INSET
  return { r, k, i }
}

/** Top-right squircle arc in local corner-box coordinates. */
export function squircleTopRightCornerPath(size: number, reverse = false) {
  const { r, k, i } = squircleCornerMetrics(size)
  // Matches squircleFramePath: vertical junction sits at x = r (= w − i in cell space).
  return reverse
    ? `M ${r}, ${i + r} C ${r}, ${i + k} ${r - k}, ${i} 0, ${i}`
    : `M 0, ${i} C ${r - k}, ${i} ${r}, ${i + k} ${r}, ${i + r}`
}

/** Bottom-left squircle arc in local corner-box coordinates (drawn top → bottom, left → right). */
export function squircleBottomLeftCornerPath(size: number, reverse = false) {
  const { r, k, i } = squircleCornerMetrics(size)
  return reverse
    ? `M ${i + r}, ${r} C ${i + k}, ${r} ${i}, ${r - k} ${i}, 0`
    : `M ${i}, 0 C ${i}, ${r - k} ${i + k}, ${r} ${i + r}, ${r}`
}

/** Top-left squircle arc in local corner-box coordinates. */
export function squircleTopLeftCornerPath(size: number, reverse = false) {
  const { r, k, i } = squircleCornerMetrics(size)
  return reverse
    ? `M ${size - r}, ${i + r} C ${size - r}, ${i + k} ${size - r + k}, ${i} ${size}, ${i}`
    : `M ${size}, ${i} C ${size - r + k}, ${i} ${size - r}, ${i + k} ${size - r}, ${i + r}`
}

/** Bottom-right squircle arc in local corner-box coordinates. */
export function squircleBottomRightCornerPath(size: number, reverse = false) {
  const { r, k, i } = squircleCornerMetrics(size)
  return reverse
    ? `M ${size - i - r}, ${r} C ${size - i - k}, ${r} ${size - i}, ${r - k} ${size - i}, 0`
    : `M ${size - i}, 0 C ${size - i}, ${r - k} ${size - i - k}, ${r} ${size - i - r}, ${r}`
}

/**
 * Frame paths in pixel space, stroke inset by half a pixel:
 * - default: top edge + top-right squircle + right edge
 * - omitTop: same minus the top edge (row above supplies the shared rule)
 * - omitRight: top edge + top-right squircle only (neighbor supplies the column rule)
 * - bottom-left: bottom edge + squircle, left side rises to the top
 * - bottom-right: bottom edge + squircle, right side rises to the top
 */
export function squircleFramePath(
  w: number,
  h: number,
  corner: SquircleFrameCorner | undefined,
  omitTop: boolean,
  omitRight = false,
) {
  const r = Math.min(SQUIRCLE_FRAME_RADIUS, w / 2, h / 2)
  const k = r * SQUIRCLE_FRAME_SMOOTHING
  const i = SQUIRCLE_FRAME_STROKE_INSET

  if (corner === 'bottom-left') {
    return `M${w},${h - i} H${i + r} C${i + k},${h - i} ${i},${h - i - k} ${i},${h - i - r} V0`
  }
  if (corner === 'bottom-right') {
    return `M0,${h - i} H${w - i - r} C${w - i - k},${h - i} ${w - i},${h - i - k} ${w - i},${h - i - r} V0`
  }
  const topRightCorner = `C${w - i - k},${i} ${w - i},${i + k} ${w - i},${i + r}`
  const topRight = omitRight ? topRightCorner : `${topRightCorner} V${h}`
  return omitTop
    ? `M${w - i - r},${i} ${topRight}`
    : `M0,${i} H${w - i - r} ${topRight}`
}

function snapDevicePx(value: number) {
  const dpr = window.devicePixelRatio || 1
  return Math.round(value * dpr) / dpr
}

export function SquircleCellFrame({
  borderName,
  corner,
  omitTop = false,
  omitRight = false,
  fadeTopEdge = false,
  fadeBottomEdge = false,
  strokeClassName = 'stroke-border',
  animateDraw = false,
  drawReverse = false,
  inView = false,
  drawDelayMs = 0,
  drawDurationMs = SQUIRCLE_DRAW_DURATION_MS,
}: {
  borderName?: string
  corner?: SquircleFrameCorner
  omitTop?: boolean
  /** Skip the descending right edge — used when the neighbor cell draws the column rule. */
  omitRight?: boolean
  fadeTopEdge?: boolean
  fadeBottomEdge?: boolean
  strokeClassName?: string
  animateDraw?: boolean
  /** Draw from the path's end instead of its start (e.g. top-down for `bottom-left`). */
  drawReverse?: boolean
  inView?: boolean
  drawDelayMs?: number
  drawDurationMs?: number
}) {
  const svgRef = useRef<SVGSVGElement>(null)
  const pathRef = useRef<SVGPathElement>(null)
  const [pathLength, setPathLength] = useState(0)
  const geometryRef = useRef({ corner, omitTop, omitRight, animateDraw })
  geometryRef.current = { corner, omitTop, omitRight, animateDraw }

  const drawn = !animateDraw || inView

  useLayoutEffect(() => {
    const svg = svgRef.current
    const path = pathRef.current
    if (!svg || !path) return

    const applyBox = (width: number, height: number) => {
      if (width < 1 || height < 1) return
      const { corner, omitTop, omitRight, animateDraw } = geometryRef.current
      const w = snapDevicePx(width)
      const h = snapDevicePx(height)
      path.setAttribute('d', squircleFramePath(w, h, corner, omitTop, omitRight))
      if (animateDraw) {
        setPathLength(path.getTotalLength())
      }
    }

    const measureFromRect = () => {
      const { width, height } = svg.getBoundingClientRect()
      applyBox(width, height)
    }

    measureFromRect()

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (!entry) return
      const box = entry.contentBoxSize.at(0)
      if (box) {
        applyBox(box.inlineSize, box.blockSize)
        return
      }
      applyBox(entry.contentRect.width, entry.contentRect.height)
    })
    observer.observe(svg)
    return () => observer.disconnect()
  }, [corner, omitTop, omitRight])

  return (
    <svg
      ref={svgRef}
      aria-hidden
      data-border-name={borderName}
      className={cn(
        'pointer-events-none absolute inset-0 size-full overflow-visible',
        strokeClassName,
        fadeTopEdge &&
          '[mask-image:linear-gradient(to_bottom,transparent,black_32%)] [-webkit-mask-image:linear-gradient(to_bottom,transparent,black_32%)]',
        fadeBottomEdge &&
          '[mask-image:linear-gradient(to_top,transparent,black_32%)] [-webkit-mask-image:linear-gradient(to_top,transparent,black_32%)]',
      )}
      shapeRendering="geometricPrecision"
    >
      <path
        ref={pathRef}
        d=""
        fill="none"
        strokeWidth={1}
        style={
          animateDraw && pathLength > 0
            ? {
                strokeDasharray: pathLength,
                strokeDashoffset: drawn
                  ? 0
                  : drawReverse
                    ? -pathLength
                    : pathLength,
                transition: drawn
                  ? `stroke-dashoffset ${drawDurationMs}ms ease-in-out ${drawDelayMs}ms`
                  : 'none',
              }
            : undefined
        }
      />
    </svg>
  )
}
